import { useState } from "react";
import {
    Sparkles,
    Trash2,
    RefreshCw,
    Lock,
    Check,
    ChevronDown,
    ChevronUp,
} from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

// ─────────────────────────────────────────────────────────────
// ValuationPanel — internal-only "Évaluation économique de la
// mission XV". 5 blocks : Lecture IA · Facteurs · Décision ·
// Historique · Rentabilité réelle. Strictly admin-only.
// ─────────────────────────────────────────────────────────────

const EUR = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
});
const fmtEUR = (n) => EUR.format(Number(n) || 0);

const STATUS_LABELS = {
    proposed: "Proposé",
    validated: "Validé",
    invoiced: "Facturé",
    collected: "Encaissé",
};

const MODE_LABELS = {
    forfait: "Forfait",
    forfait_success: "Forfait + Success fee",
    success: "Success fee",
    mixte: "Mixte",
};

const CRITERIA_LABELS = {
    operation_type: "Type d'opération",
    operation_amount_band: "Montant",
    complexity: "Complexité",
    stakeholders_band: "Parties prenantes",
    urgency: "Urgence",
    network_need: "Besoin réseau XV",
    international_dim: "International",
    success_fee_potential: "Potentiel success fee",
};

const PRETTY_BAND = {
    "<500k": "< 500 k€",
    "500k-1M": "500 k€ — 1 M€",
    "1M-5M": "1 M€ — 5 M€",
    "5M-20M": "5 M€ — 20 M€",
    ">20M": "> 20 M€",
};

function prettyValue(key, val) {
    if (val === null || val === undefined || val === "") return "—";
    if (key === "international_dim") return val ? "Oui" : "Non";
    if (key === "operation_amount_band") return PRETTY_BAND[val] || val;
    return val;
}

export default function ValuationPanel({ dossier, onUpdated }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [collapsed, setCollapsed] = useState(false);
    const v = dossier.valuation || {};
    const ai = v.ai || null;
    const dec = v.decision || {};
    const adj = v.adjustments || [];

    const [form, setForm] = useState({
        fees_retained: dec.fees_retained || "",
        success_fee_retained_pct: dec.success_fee_retained_pct || "",
        remuneration_mode: dec.remuneration_mode || "forfait",
        status: dec.status || "proposed",
        motive: "",
    });

    const change = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const generate = async () => {
        setBusy(true);
        setErr("");
        try {
            const { data } = await cockpit.post(
                `/dossiers/${dossier.id}/valuation/generate`,
            );
            onUpdated?.({ ...dossier, valuation: data.valuation });
            // pre-fill form with AI suggestion if no decision yet
            if (!dec.fees_retained) {
                setForm((f) => ({
                    ...f,
                    fees_retained: data.valuation.ai?.fees_recommended || 0,
                    success_fee_retained_pct:
                        data.valuation.ai?.success_fee_pct_min || 0,
                }));
            }
        } catch (e) {
            setErr(
                e?.response?.data?.detail
                || "Valorisation IA indisponible — réessayez.",
            );
        } finally {
            setBusy(false);
        }
    };

    const saveDecision = async (e) => {
        e?.preventDefault?.();
        setBusy(true);
        setErr("");
        try {
            const payload = {
                fees_retained: Number(form.fees_retained) || 0,
                success_fee_retained_pct:
                    Number(form.success_fee_retained_pct) || 0,
                remuneration_mode: form.remuneration_mode,
                status: form.status,
                motive: form.motive?.trim() || "",
            };
            const { data } = await cockpit.put(
                `/dossiers/${dossier.id}/valuation/decision`,
                payload,
            );
            onUpdated?.({ ...dossier, valuation: data.valuation });
            setForm((f) => ({ ...f, motive: "" }));
        } catch (e) {
            setErr(e?.response?.data?.detail || "Erreur");
        } finally {
            setBusy(false);
        }
    };

    const reset = async () => {
        if (!window.confirm("Réinitialiser toute la valorisation ?")) return;
        try {
            await cockpit.delete(`/dossiers/${dossier.id}/valuation`);
            const next = { ...dossier };
            delete next.valuation;
            onUpdated?.(next);
            setForm({
                fees_retained: "",
                success_fee_retained_pct: "",
                remuneration_mode: "forfait",
                status: "proposed",
                motive: "",
            });
        } catch (e) {
            setErr(e?.response?.data?.detail || "Erreur");
        }
    };

    const formatDate = (iso) => {
        if (!iso) return "";
        try {
            return new Date(iso).toLocaleString("fr-FR", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return iso;
        }
    };

    const ai_suggested = ai?.fees_recommended || 0;
    const retained = Number(form.fees_retained) || dec.fees_retained || 0;
    const ecart = retained - ai_suggested;

    // Rentabilité réelle
    const estimated_hours = Number(dossier.estimated_hours) || 0;
    const hours_spent = Number(dossier.hours_spent) || 0;
    const fees_for_rate = Number(dec.fees_retained) || 0;
    const hourly_real = hours_spent > 0
        ? Math.round(fees_for_rate / hours_spent)
        : null;
    const rent_note = hourly_real === null
        ? "Heures non renseignées."
        : hourly_real >= 500
          ? "Excellente rentabilité."
          : hourly_real >= 300
            ? "Rentabilité saine."
            : hourly_real >= 150
              ? "Rentabilité acceptable."
              : "Rentabilité sous le seuil — revoir le périmètre.";

    return (
        <section
            data-testid="valuation-panel"
            className="border-t border-white/[0.06] py-12"
        >
            {/* Header */}
            <div className="flex items-baseline justify-between flex-wrap gap-4 mb-7">
                <div>
                    <div className="overline text-[#d4af37]/70 mb-3 flex items-center gap-2">
                        <Lock strokeWidth={1.1} size={11} />
                        Évaluation économique · interne
                    </div>
                    <h2
                        className="font-serif text-[24px] sm:text-[28px] text-[#f4ecd8] leading-tight"
                        style={{ fontWeight: 400 }}
                    >
                        Valorisation de la mission XV
                    </h2>
                    <p className="mt-2 text-[12px] text-[#f4ecd8]/45 italic font-light max-w-2xl leading-relaxed">
                        Lecture économique générée par Claude à partir des
                        critères du dossier. L'IA propose, vous décidez.
                        Jamais exposé au client.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setCollapsed((v) => !v)}
                        className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/45 hover:text-[#d4af37] transition-colors duration-500 inline-flex items-center gap-2"
                    >
                        {collapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                        {collapsed ? "Déplier" : "Replier"}
                    </button>
                    {ai && (
                        <button
                            type="button"
                            onClick={reset}
                            data-testid="valuation-reset"
                            className="text-[10px] tracking-[0.28em] uppercase text-[#f4ecd8]/45 hover:text-[#e89090] transition-colors duration-500 inline-flex items-center gap-2"
                        >
                            <Trash2 strokeWidth={1.2} size={11} />
                            Effacer
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={generate}
                        disabled={busy}
                        data-testid="valuation-generate"
                        className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#d4af37]/40 text-[#d4af37] text-[10px] tracking-[0.28em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                    >
                        {ai ? <RefreshCw strokeWidth={1.2} size={12} /> : <Sparkles strokeWidth={1.2} size={12} />}
                        {busy ? "Calcul IA…" : ai ? "Régénérer" : "Générer la valorisation"}
                    </button>
                </div>
            </div>

            {err && (
                <div className="text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1 mb-6">
                    {err}
                </div>
            )}

            {collapsed && (
                <p className="text-[12px] text-[#f4ecd8]/40 italic font-light">
                    Panneau replié.
                </p>
            )}

            {!collapsed && (
                <>
                    {/* ──────── 1. Lecture économique IA ──────── */}
                    <div
                        data-testid="valuation-ai"
                        className="bg-[#070d18] border border-[#d4af37]/15 p-6 lg:p-8 mb-6"
                    >
                        <div className="overline text-[#d4af37]/65 mb-5">
                            1 · Lecture économique IA
                        </div>
                        {!ai ? (
                            <p className="text-[13px] text-[#f4ecd8]/40 italic font-light">
                                Aucune valorisation. Cliquez sur « Générer la
                                valorisation » pour obtenir une recommandation
                                d'honoraires basée sur la grille XV et les
                                critères du dossier.
                            </p>
                        ) : (
                            <div className="grid lg:grid-cols-4 gap-6">
                                <KPICell
                                    label="Type de mission"
                                    value={ai.mission_label}
                                    testid="valuation-kpi-mission_type"
                                />
                                <KPICell
                                    label="Honoraires recommandés"
                                    value={fmtEUR(ai.fees_recommended)}
                                    accent
                                    testid="valuation-kpi-fees_recommended"
                                />
                                <KPICell
                                    label="Fourchette cohérente"
                                    value={`${fmtEUR(ai.fees_min)} — ${fmtEUR(ai.fees_max)}`}
                                    testid="valuation-kpi-fees_range"
                                />
                                <KPICell
                                    label="Success fee recommandé"
                                    value={
                                        ai.success_fee_pct_max > 0
                                            ? `${ai.success_fee_pct_min}% — ${ai.success_fee_pct_max}%`
                                            : "Sur devis"
                                    }
                                    sub={ai.success_fee_kind}
                                    testid="valuation-kpi-success_fee_pct"
                                />
                                <div className="lg:col-span-4 pt-4 border-t border-white/[0.06] flex items-center justify-between gap-6">
                                    <div className="flex items-center gap-3">
                                        <span className="text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/70">
                                            Niveau de confiance
                                        </span>
                                        <div className="flex items-center gap-3 min-w-[160px]">
                                            <div className="h-[3px] w-32 bg-white/[0.06]">
                                                <div
                                                    className="h-full bg-[#d4af37]"
                                                    style={{
                                                        width: `${ai.confidence_pct}%`,
                                                    }}
                                                />
                                            </div>
                                            <span className="text-[13px] font-serif text-[#f4ecd8]/90 tabular-nums">
                                                {ai.confidence_pct}%
                                            </span>
                                        </div>
                                    </div>
                                    <span className="text-[9.5px] tracking-[0.22em] uppercase text-[#f4ecd8]/40">
                                        Générée le {formatDate(ai.generated_at)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ──────── 2. Facteurs de valorisation ──────── */}
                    <div
                        data-testid="valuation-factors"
                        className="bg-white/[0.02] border border-white/[0.06] p-6 lg:p-8 mb-6"
                    >
                        <div className="overline text-[#d4af37]/65 mb-5">
                            2 · Facteurs de valorisation
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4">
                            {Object.entries(CRITERIA_LABELS).map(([k, lbl]) => (
                                <div key={k} className="flex flex-col">
                                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-1">
                                        {lbl}
                                    </span>
                                    <span className="text-[13.5px] font-serif text-[#f4ecd8]/90 capitalize">
                                        {prettyValue(k, dossier[k])}
                                    </span>
                                </div>
                            ))}
                        </div>
                        {ai?.justification && (
                            <div className="mt-7 pt-5 border-t border-white/[0.06]">
                                <div className="overline text-[#d4af37]/55 mb-3">
                                    Justification IA
                                </div>
                                <p
                                    data-testid="valuation-justification"
                                    className="font-serif text-[14px] text-[#f4ecd8]/90 leading-[1.85] whitespace-pre-wrap italic"
                                >
                                    {ai.justification}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* ──────── 3. Décision cabinet ──────── */}
                    <form
                        data-testid="valuation-decision"
                        onSubmit={saveDecision}
                        className="bg-white/[0.02] border border-white/[0.06] p-6 lg:p-8 mb-6 space-y-5"
                    >
                        <div className="overline text-[#d4af37]/65">
                            3 · Décision cabinet
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                            <FieldNum
                                label="Honoraires retenus (€ HT)"
                                value={form.fees_retained}
                                onChange={change("fees_retained")}
                                testid="decision-fees"
                            />
                            <FieldNum
                                label="Success fee retenu (%)"
                                step="0.1"
                                value={form.success_fee_retained_pct}
                                onChange={change("success_fee_retained_pct")}
                                testid="decision-success"
                            />
                            <FieldSelect
                                label="Mode de rémunération"
                                value={form.remuneration_mode}
                                onChange={change("remuneration_mode")}
                                options={Object.entries(MODE_LABELS)}
                                testid="decision-mode"
                            />
                            <FieldSelect
                                label="Statut"
                                value={form.status}
                                onChange={change("status")}
                                options={Object.entries(STATUS_LABELS)}
                                testid="decision-status"
                            />
                        </div>
                        {/* Écart d'appréciation */}
                        {ai && (
                            <div className="bg-[#070d18] border border-white/[0.06] p-5 grid sm:grid-cols-3 gap-5 items-center">
                                <div>
                                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-1">
                                        Suggestion IA
                                    </div>
                                    <div className="font-serif text-[18px] text-[#f4ecd8]/90 tabular-nums">
                                        {fmtEUR(ai_suggested)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-1">
                                        Retenu
                                    </div>
                                    <div className="font-serif text-[18px] text-[#d4af37] tabular-nums">
                                        {fmtEUR(retained)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-1">
                                        Écart
                                    </div>
                                    <div
                                        className={`font-serif text-[18px] tabular-nums ${
                                            ecart > 0
                                                ? "text-[#a3d39c]"
                                                : ecart < 0
                                                  ? "text-[#e89090]"
                                                  : "text-[#f4ecd8]/70"
                                        }`}
                                    >
                                        {ecart > 0 ? "+" : ""}
                                        {fmtEUR(ecart)}
                                    </div>
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="block text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/65 mb-2">
                                Motif de l'écart / commentaire
                            </label>
                            <input
                                type="text"
                                data-testid="decision-motive"
                                value={form.motive}
                                onChange={change("motive")}
                                placeholder="ex : remise stratégique, dossier sensible…"
                                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8] placeholder-[#f4ecd8]/25"
                            />
                        </div>
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="decision-save"
                                className="inline-flex items-center gap-3 px-6 py-3 border border-[#d4af37]/40 text-[#d4af37] text-[10px] tracking-[0.28em] uppercase hover:bg-[#d4af37] hover:text-[#070d18] transition-all duration-500 disabled:opacity-40"
                            >
                                <Check strokeWidth={1.2} size={12} />
                                Enregistrer la décision
                            </button>
                        </div>
                    </form>

                    {/* ──────── 4. Historique des ajustements ──────── */}
                    {adj.length > 0 && (
                        <div
                            data-testid="valuation-history"
                            className="bg-white/[0.02] border border-white/[0.06] p-6 lg:p-8 mb-6"
                        >
                            <div className="overline text-[#d4af37]/65 mb-5">
                                4 · Historique des ajustements
                            </div>
                            <ul className="space-y-3">
                                {adj.slice().reverse().map((a) => (
                                    <li
                                        key={a.id}
                                        className="grid grid-cols-[auto_1fr_auto_auto] gap-5 items-center border-b border-white/[0.04] pb-3 last:border-b-0 last:pb-0"
                                    >
                                        <span className="text-[10px] tracking-[0.22em] uppercase text-[#f4ecd8]/40 tabular-nums whitespace-nowrap">
                                            {formatDate(a.at)}
                                        </span>
                                        <span className="text-[13px] font-serif text-[#f4ecd8]/85 italic">
                                            {a.motive || "—"}
                                        </span>
                                        <span className="text-[12px] text-[#f4ecd8]/55 tabular-nums">
                                            IA : {fmtEUR(a.ai_suggested)}
                                        </span>
                                        <span className="text-[12.5px] text-[#d4af37] tabular-nums">
                                            Retenu : {fmtEUR(a.retained)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* ──────── 5. Rentabilité réelle ──────── */}
                    <div
                        data-testid="valuation-profitability"
                        className="bg-[#070d18] border border-white/[0.06] p-6 lg:p-8"
                    >
                        <div className="overline text-[#d4af37]/65 mb-5">
                            5 · Rentabilité réelle
                        </div>
                        <div className="grid sm:grid-cols-4 gap-6">
                            <KPICell
                                label="Temps estimé"
                                value={`${estimated_hours} h`}
                            />
                            <KPICell
                                label="Temps réel"
                                value={`${hours_spent} h`}
                            />
                            <KPICell
                                label="Honoraires retenus"
                                value={fmtEUR(fees_for_rate)}
                            />
                            <KPICell
                                label="Valorisation horaire réelle"
                                value={
                                    hourly_real !== null
                                        ? `${fmtEUR(hourly_real)} / h`
                                        : "—"
                                }
                                accent
                            />
                        </div>
                        <p className="mt-5 text-[13px] font-serif italic text-[#f4ecd8]/75 leading-relaxed border-l-2 border-[#d4af37]/40 pl-4">
                            {rent_note}
                        </p>
                    </div>
                </>
            )}
        </section>
    );
}

function KPICell({ label, value, sub, accent, testid }) {
    return (
        <div data-testid={testid}>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#d4af37]/55 mb-2">
                {label}
            </div>
            <div
                className={`font-serif text-[18px] sm:text-[20px] leading-tight ${
                    accent ? "text-[#d4af37]" : "text-[#f4ecd8]"
                }`}
                style={{ fontWeight: 400 }}
            >
                {value}
            </div>
            {sub && (
                <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-[#f4ecd8]/35">
                    {sub}
                </div>
            )}
        </div>
    );
}

function FieldNum({ label, value, onChange, step = "1", testid }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/65 mb-2">
                {label}
            </label>
            <input
                type="number"
                min={0}
                step={step}
                data-testid={testid}
                value={value}
                onChange={onChange}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[15px] font-serif text-[#f4ecd8]"
            />
        </div>
    );
}

function FieldSelect({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.26em] uppercase text-[#d4af37]/65 mb-2">
                {label}
            </label>
            <select
                value={value}
                onChange={onChange}
                data-testid={testid}
                className="w-full bg-transparent border-b border-white/15 focus:border-[#d4af37] outline-none py-2 text-[14px] font-serif text-[#f4ecd8]"
            >
                {options.map(([k, l]) => (
                    <option key={k} value={k} className="bg-[#0b1220]">
                        {l}
                    </option>
                ))}
            </select>
        </div>
    );
}
